/**
 * LeaveGuard — confirmation modal that intercepts attempts to leave the
 * placement test mid-flow. Implements placement-leave-guard-handoff.md
 * from the Claude Design bundle.
 *
 * Three trigger surfaces:
 *   1. Capture-phase click on internal <a> elements (Link, plain anchors).
 *      Coexists with AuthGate — if AuthGate already preventDefaulted the
 *      click, we step aside.
 *   2. Browser Back / Forward (popstate) — when armed, we push a guard
 *      entry on top of the current history state; popstate re-pushes and
 *      opens the modal. "Yes, leave" runs history.go(-2) to skip past
 *      our entry.
 *   3. Tab close / refresh / URL bar edit — native beforeunload prompt.
 *      Modern browsers ignore custom strings, so we just trigger the
 *      browser's default dialog.
 *
 * Armed state lives in sessionStorage under `sag-placement-started` so
 * the guard survives full-page refreshes (the beforeunload listener
 * reads the flag directly — React hasn't rendered yet at that point).
 *
 * Arming contract: callers use the `useLeaveGuard()` hook's arm() /
 * disarm() methods. DiagnosticPlayShell arms on mount and disarms on
 * unmount — natural placement completion or programmatic exit (via the
 * HUD's ExitConfirmDialog) unmounts the shell and clears the flag.
 */
import * as React from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/leave-guard.css";

const STORAGE_KEY = "sag-placement-started";

type PendingNav = () => void;

/**
 * Which confirmation copy to show. "exit" (default) is the leave/abandon
 * framing used by link clicks, the Back button, and the Exit button.
 * "restart" is used by the pause menu's "Restart placement" — it's not
 * leaving, it's starting over now, so the copy must not say "leave".
 */
type LeaveGuardVariant = "exit" | "restart";

interface LeaveGuardContextValue {
  arm(): void;
  disarm(): void;
  isArmed(): boolean;
  /**
   * Imperatively open the leave-guard modal with a custom action. Used by
   * the placement HUD's Exit button: clicking Exit shouldn't *try* to
   * navigate (which would just defer to the click interceptor and lose
   * the API call that ends the play); it should show the same modal and,
   * on confirm, run the supplied callback (e.g. POST /end → navigate).
   *
   * Pass `{ variant: "restart" }` to swap in the restart-framed copy while
   * keeping the same confirm flow.
   */
  prompt(action: () => void, opts?: { variant?: LeaveGuardVariant }): void;
}

const LeaveGuardContext = React.createContext<LeaveGuardContextValue | null>(null);

function readArmed(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeArmed(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(STORAGE_KEY, "true");
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* sandboxed iframes etc. */
  }
}

export function LeaveGuardProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();

  // React state mirrors sessionStorage so the modal renders reactively;
  // sessionStorage is the durable source of truth across refreshes.
  // On initial mount, auto-clear a stale flag if we're not on the
  // diagnostic play route (covers crash, tab kill, manual URL change).
  const [armed, setArmed] = React.useState<boolean>(() => {
    const stored = readArmed();
    if (stored && !window.location.pathname.startsWith("/diagnostic/play")) {
      writeArmed(false);
      return false;
    }
    return stored;
  });
  const armedRef = React.useRef(armed);
  armedRef.current = armed;

  const [pending, setPending] = React.useState<PendingNav | null>(null);
  const pendingRef = React.useRef<PendingNav | null>(null);
  pendingRef.current = pending;

  // Copy variant for the currently-open modal. Reset to "exit" on every close
  // so the implicit triggers (link click, Back) always get the leave framing;
  // only prompt({ variant: "restart" }) opts into the restart copy.
  const [variant, setVariant] = React.useState<LeaveGuardVariant>("exit");

  // Set true while a confirmed leave is in flight, so re-entries from the
  // navigation we just authorised don't re-prompt.
  const leavingRef = React.useRef(false);

  const lastFocusRef = React.useRef<HTMLElement | null>(null);

  const arm = React.useCallback(() => {
    writeArmed(true);
    setArmed(true);
    armedRef.current = true;
    // Push the guard history entry NOW (not in an effect), so a Back press
    // immediately after arming has something to pop. Doing this in a [armed]
    // effect was racy under StrictMode double-mount — the effect's cleanup
    // tore down the popstate listener between the synthetic unmount and
    // remount, briefly leaving Back unguarded.
    try {
      window.history.pushState({ leaveGuard: true }, "", window.location.href);
    } catch {
      /* sandboxed iframes etc. */
    }
  }, []);

  const disarm = React.useCallback(() => {
    writeArmed(false);
    setArmed(false);
    armedRef.current = false;
  }, []);

  const prompt = React.useCallback((action: () => void, opts?: { variant?: LeaveGuardVariant }) => {
    lastFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setVariant(opts?.variant ?? "exit");
    setPending(() => action);
  }, []);

  const ctx = React.useMemo<LeaveGuardContextValue>(
    () => ({ arm, disarm, isArmed: () => armedRef.current, prompt }),
    [arm, disarm, prompt],
  );

  // ─── Trigger 1: capture-phase click interceptor ───────────────────
  React.useEffect(() => {
    function onClick(e: MouseEvent) {
      if (leavingRef.current) return;
      if (!armedRef.current) return;
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0) return;

      const target = e.target;
      if (!(target instanceof Element)) return;
      const a = target.closest("a");
      if (!a) return;

      const href = a.getAttribute("href");
      if (!href) return;
      if (href.startsWith("#")) return;
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;

      // Same-pathname links shouldn't prompt — the user isn't leaving.
      // Resolve href against current origin to compare pathnames.
      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (url.pathname === window.location.pathname) return;
      // External origins fall through to beforeunload anyway, but we
      // still prompt for them (the test is being abandoned either way).

      e.preventDefault();
      e.stopPropagation();
      lastFocusRef.current = a as HTMLElement;
      setVariant("exit");
      setPending(() => () => {
        if (url.origin === window.location.origin) {
          navigate(url.pathname + url.search + url.hash);
        } else {
          window.location.href = href;
        }
      });
    }
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [navigate]);

  // ─── Trigger 2: browser Back / Forward ────────────────────────────
  // Persistent popstate listener — gated on armedRef at fire-time rather
  // than re-registered on every arm/disarm. Re-registering was racy under
  // StrictMode double-mount because the cleanup briefly tore the listener
  // down between synthetic unmount and remount. Pushing the guard entry
  // lives in arm() (synchronous with the user's commitment to start), so
  // a Back press immediately after arming finds something to pop.
  React.useEffect(() => {
    function onPopState() {
      if (leavingRef.current) return;
      if (!armedRef.current) return;
      try {
        window.history.pushState({ leaveGuard: true }, "", window.location.href);
      } catch {
        /* no-op */
      }
      setVariant("exit");
      setPending(() => () => {
        // -2 skips our re-pushed guard entry AND the entry the user was
        // trying to land on, depositing them on the prior page.
        window.history.go(-2);
      });
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // ─── Trigger 3: tab close / refresh ───────────────────────────────
  // beforeunload reads sessionStorage directly so it's correct even if
  // the React tree hasn't rendered (e.g. immediately after a refresh).
  React.useEffect(() => {
    function onBefore(e: BeforeUnloadEvent) {
      if (leavingRef.current) return;
      if (!readArmed()) return;
      e.preventDefault();
      e.returnValue = ""; // required for Chrome
    }
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, []);

  // ─── Modal lifecycle ──────────────────────────────────────────────
  const isOpen = pending !== null;

  // Body scroll lock + Esc.
  React.useLayoutEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("leave-guard-open");
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") stay();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("leave-guard-open");
      document.removeEventListener("keydown", onKey);
    };
    // stay is stable via useCallback below. (No react-hooks/exhaustive-deps
    // disable directive here: the plugin isn't registered in eslint.config.mjs,
    // so the directive itself errors as an unknown rule once this file is linted.)
  }, [isOpen]);

  // Auto-close the modal when the URL pathname genuinely changes (not on
  // every popstate — a re-pushed guard entry leaves pathname unchanged but
  // can still bump React Router's location.key, so we compare strings via
  // a ref instead of relying on the dep array).
  const prevPathnameRef = React.useRef(location.pathname);
  React.useEffect(() => {
    if (location.pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = location.pathname;
      setPending(null);
      // Auto-disarm when navigating away from the diagnostic play route.
      // Covers edge cases where the shell unmount cleanup didn't fire
      // (crash, tab kill, stale sessionStorage).
      if (armedRef.current && !location.pathname.startsWith("/diagnostic/play")) {
        disarm();
      }
    }
  });

  // Focus the safe default (Stay) on open.
  const stayRef = React.useRef<HTMLButtonElement>(null) as React.RefObject<HTMLButtonElement>;
  React.useEffect(() => {
    if (isOpen && stayRef.current) {
      const id = window.setTimeout(() => stayRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return;
  }, [isOpen]);

  const stay = React.useCallback(() => {
    setPending(null);
    const last = lastFocusRef.current;
    if (last && typeof last.focus === "function") {
      try {
        last.focus();
      } catch {
        /* no-op */
      }
    }
  }, []);

  const leave = React.useCallback(() => {
    const fn = pendingRef.current;
    setPending(null);
    leavingRef.current = true;
    if (typeof fn === "function") fn();
  }, []);

  return (
    <LeaveGuardContext.Provider value={ctx}>
      {children}
      {isOpen &&
        createPortal(
          <LeaveGuardModal variant={variant} onStay={stay} onLeave={leave} stayRef={stayRef} />,
          document.body,
        )}
    </LeaveGuardContext.Provider>
  );
}

export function useLeaveGuard(): LeaveGuardContextValue {
  const ctx = React.useContext(LeaveGuardContext);
  if (!ctx) {
    throw new Error("useLeaveGuard must be used within <LeaveGuardProvider>");
  }
  return ctx;
}

interface ModalProps {
  variant: LeaveGuardVariant;
  onStay: () => void;
  onLeave: () => void;
  stayRef: React.RefObject<HTMLButtonElement>;
}

interface ModalCopy {
  title: React.ReactNode;
  body: React.ReactNode;
  stay: string;
  leave: string;
  foot: React.ReactNode;
}

const MODAL_COPY: Record<LeaveGuardVariant, ModalCopy> = {
  exit: {
    title: (
      <>
        Sure you want to <span className="underline">leave</span>?
      </>
    ),
    body: (
      <>
        Your <strong>placement progress won't save</strong> — you'll have to start the test over
        next time. Just a few more questions to go.
      </>
    ),
    stay: "Stay and finish",
    leave: "Yes, leave the test",
    foot: (
      <>
        Tip: <strong>tap "Stay"</strong> if you got here by accident.
      </>
    ),
  },
  restart: {
    title: (
      <>
        Start <span className="underline">over</span>?
      </>
    ),
    body: (
      <>
        This begins a <strong>fresh placement from Round 1</strong>. Your progress in this run won't
        be saved.
      </>
    ),
    stay: "Keep playing",
    leave: "Yes, start over",
    foot: (
      <>
        Tip: <strong>tap "Keep playing"</strong> if you got here by accident.
      </>
    ),
  },
};

function LeaveGuardModal({ variant, onStay, onLeave, stayRef }: ModalProps): React.ReactElement {
  const copy = MODAL_COPY[variant];
  return (
    <div
      className="leave-guard__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-guard-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onStay();
      }}
    >
      <div className="leave-guard__modal">
        <span className="leave-guard__sticker">Mid-test</span>
        <button type="button" className="leave-guard__close" aria-label="Close" onClick={onStay}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        <div className="leave-guard__head">
          <span className="leave-guard__eyebrow">Heads up</span>
          <h2 id="leave-guard-title" className="leave-guard__title">
            {copy.title}
          </h2>
          <p className="leave-guard__body">{copy.body}</p>
        </div>

        <div className="leave-guard__ctas">
          <button ref={stayRef} type="button" className="stay" onClick={onStay}>
            {copy.stay}
            <span className="arc">
              <ArrowRight />
            </span>
          </button>
          <button type="button" className="leave" onClick={onLeave}>
            {copy.leave}
            <span className="arc">
              <ArrowRight />
            </span>
          </button>
        </div>

        <div className="leave-guard__foot">{copy.foot}</div>
      </div>
    </div>
  );
}

function ArrowRight(): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
